import React, { useState, useRef } from 'react';
import AppCardHeading from './AppCardHeading';
import AppCardContent from './AppCardContent';
import AppCardFooter from './AppCardFooter';
import AppSectionLoader from '../AppSectionLoader';

export interface AppCardProps {
    // Icon and heading props
    icon?: string | React.ReactNode;
    iconColor?: string;
    iconClass?: string;
    heading?: string;
    headingBg?: string;
    styles?: string;

    // Control props
    closeable?: string;
    reloadable?: string;
    fullScreen?: boolean;
    isFullscreen?: boolean;

    // Custom content flags
    customActions?: boolean;
    customButtons?: boolean;
    customText?: boolean;
    customComponent?: boolean;

    // Tabs
    withTabs?: string;
    tabs?: string;

    // Layout and styling
    colClass?: string;
    colClasses?: string;
    customClasses?: string;
    contentCustomClass?: string;
    fullBlock?: boolean;

    // Content flags
    footer?: boolean;
    content?: boolean;

    // Event handlers
    onFullscreenChange?: (isFullscreen: boolean) => void;
    onChangeTabCallback?: (tabKey: string) => void;
    onReload?: () => void;
    onClose?: () => void;

    // Children
    children?: React.ReactNode;
    customActionsSlot?: React.ReactNode;
    customButtonsSlot?: React.ReactNode;
    customTextSlot?: React.ReactNode;
    customComponentSlot?: React.ReactNode;
    footerSlot?: React.ReactNode;
}

const AppCard: React.FC<AppCardProps> = ({
    // Display props
    icon,
    iconColor,
    iconClass,
    heading,
    headingBg,
    styles,

    // Control flags
    closeable = '',
    reloadable = '',
    fullScreen = false,
    isFullscreen = false,

    // Custom content flags
    customActions = false,
    customButtons = false,
    customText = false,
    customComponent = false,

    // Tab configuration
    withTabs = '',
    tabs = '',

    // Styling props
    colClasses,
    customClasses,
    contentCustomClass,
    fullBlock = false,

    // Layout flags
    footer = false,
    content = true,

    // Event handlers
    onFullscreenChange,
    onChangeTabCallback,
    onReload: onReloadProp,
    onClose: onCloseProp,

    // Content slots
    children,
    customActionsSlot,
    customButtonsSlot,
    customTextSlot,
    customComponentSlot,
    footerSlot
}) => {
    const [reload, setReload] = useState(false);
    const [closed, setClosed] = useState(false);
    const [isFullscreenActive, setIsFullscreenActive] = useState(isFullscreen);
    const fullscreenRef = useRef<HTMLDivElement>(null);

    const handleFullscreenChange = (fullscreen: boolean) => {
        setIsFullscreenActive(fullscreen);
        onFullscreenChange?.(fullscreen);
    };

    const toggleFullscreen = () => {
        if (fullscreenRef.current) {
            if (!document.fullscreenElement) {
                fullscreenRef.current
                    .requestFullscreen()
                    .then(() => {
                        handleFullscreenChange(true);
                    })
                    .catch(() => {
                        handleFullscreenChange(false);
                    });
            } else {
                document
                    .exitFullscreen()
                    .then(() => {
                        handleFullscreenChange(false);
                    })
                    .catch(() => {
                        handleFullscreenChange(true);
                    });
            }
        }
    };

    const handleReload = () => {
        setReload(true);
        setTimeout(() => {
            setReload(false);
        }, 1500);
        onReloadProp?.();
    };

    const handleClose = () => {
        setClosed(true);
        onCloseProp?.();
    };

    const handleChangeTab = (tabKey: string) => {
        onChangeTabCallback?.(tabKey);
    };

    const containerClasses = [colClasses, colClasses ? 'flex' : ''].filter(Boolean).join(' ');

    const cardClasses = ['app-card', customClasses, closed ? 'd-none' : ''].filter(Boolean).join(' ');

    return (
        <div className={containerClasses || undefined}>
            <div ref={fullscreenRef}>
                <div className={cardClasses}>
                    <AppCardHeading
                        icon={icon}
                        iconColor={iconColor}
                        iconClass={iconClass}
                        heading={heading}
                        headingBg={headingBg}
                        styles={styles}
                        closeable={closeable}
                        reloadable={reloadable}
                        fullScreen={fullScreen}
                        isFullscreen={isFullscreenActive}
                        customActions={customActions}
                        customButtons={customButtons}
                        customText={customText}
                        customComponent={customComponent}
                        withTabs={withTabs}
                        tabs={tabs}
                        onToggleFullscreen={toggleFullscreen}
                        onReload={handleReload}
                        onClose={handleClose}
                        onChangeTabCallback={handleChangeTab}
                        customButtonsSlot={customButtonsSlot}
                        customTextSlot={customTextSlot}
                        customComponentSlot={customComponentSlot}
                    >
                        {customActionsSlot}
                    </AppCardHeading>

                    {content && (
                        <AppCardContent
                            extraClass={contentCustomClass ? [contentCustomClass] : []}
                            fullBlock={fullBlock}
                        >
                            {children}
                        </AppCardContent>
                    )}

                    {footer && <AppCardFooter>{footerSlot}</AppCardFooter>}

                    <AppSectionLoader status={reload} />
                </div>
            </div>
        </div>
    );
};

export default AppCard;
